import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../utils/api';
import { addStaticSkills } from './utils';
import { normalizeSkillCatalog } from './characterBuild';
import { updateCalculatedFields, shallowEqualSkills } from './characterCalculations';

const EMPTY_SKILLS = [];
const id = value => value == null ? null : Number(value);
const matches = (character, scope) => character &&
    id(character.characterId) === scope.characterId && id(character.transformId) === scope.transformId;

// SP cost per transform tier — mirrors the backend (routes/skills.js) so a
// freshly-applied transform costs the same here as it will after a save+reload.
const TRANSFORM_TIER_COST = {
    0: { passive: [0, 0],  active: [0, 0] },
    1: { passive: [5, 0],  active: [2, 3] },
    2: { passive: [10, 0], active: [3, 5] },
    3: { passive: [15, 0], active: [5, 10] },
    4: { passive: [20, 0], active: [10, 10] },
    5: { passive: [30, 0], active: [20, 10] },
    6: { passive: [40, 0], active: [20, 20] },
};

// Convert a transform ability (from GET /transforms/:id) into the skill-catalog
// shape the skills UI expects: skillset 8, with its primary cost derived from tier.
function transformAbilityToCatalogSkill(a) {
    const tier = a.tier ?? 0;
    const [base, increment] =
        (a.isPassive ? TRANSFORM_TIER_COST[tier]?.passive : TRANSFORM_TIER_COST[tier]?.active) || [0, 0];
    return {
        skillId: Number(a.skillId),
        name: a.name,
        skillset: 8,
        primaryBaseCost: base,
        primaryIncrementCost: increment,
        secondaryBaseCost: null,
        tertiaryBaseCost: null,
        secondaryIncrementCost: null,
        tertiaryIncrementCost: null,
        description: a.notes,
        tier,
        isPassive: !!a.isPassive,
        isDefaultUnlocked: !!a.isDefaultUnlocked,
        mustBePrimary: a.mustBePrimary ?? null,
        prereqs: [],
    };
}

function shallowEqualCharacter(a, b) {
    if (a === b) return true;
    const keys = [
        "bodyPoints","maxArmor","dexArmor",
        "agilityPoints","arcanePoints","naturePoints","spiritPoints",
        "staminaPoints","thaumaturgyPoints",
        "skillPointsSpent","skillPointsUnspent"
    ];
    for (const k of keys) if ((a?.[k] || 0) !== (b?.[k] || 0)) return false;
    return shallowEqualSkills(a?.skills, b?.skills);
}


// One catalog shared by the form, skills panel, print action and draft loading.
// Fetch the selected transform explicitly: the character endpoint may still
// reflect the saved transform while the editor has an unsaved reassignment.
export default function useCharacterSkillCatalog({ currentCharacter, setCurrentCharacter, token }) {
    const characterId = id(currentCharacter?.characterId);
    const transformId = id(currentCharacter?.transformId);
    const scope = useMemo(() => ({ characterId, transformId, token }), [characterId, transformId, token]);
    const currentScope = useRef(scope);
    currentScope.current = scope;
    const synced = useRef(null);
    const [state, setState] = useState({ scope: null, skills: EMPTY_SKILLS, error: null });
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const active = () => !cancelled && currentScope.current === scope;
        // Retain the initial transform even if it changes before the first fetch
        // finishes; only a real reassignment should clear transform purchases.
        if (synced.current?.characterId !== characterId) {
            synced.current = { characterId, transformId, skills: EMPTY_SKILLS };
        }
        setState(prev => ({ scope, loading: true, error: null,
            skills: prev.scope?.characterId === characterId && prev.scope?.token === token
                ? prev.skills : EMPTY_SKILLS }));
        if (!characterId) return () => { cancelled = true; };
        (async () => {
            try {
                const [fetched, transform] = await Promise.all([
                    api.fetchSkills({ characterId }),
                    transformId == null ? null : api.fetchTransform(transformId),
                ]);
                if (!active()) return;
                const base = normalizeSkillCatalog(fetched);
                if (transformId != null && !Array.isArray(transform?.abilities)) {
                    throw new Error('Invalid transform catalog.');
                }
                const skills = normalizeSkillCatalog([
                    ...base.filter(skill => skill.skillset !== 8),
                    ...(transform?.abilities || []).map(ability => ({
                        ...transformAbilityToCatalogSkill(ability),
                        mustBePrimary: ability.mustBePrimary ?? base.find(skill => skill.skillId === Number(ability.skillId))?.mustBePrimary ?? null,
                        prereqs: base.find(skill => skill.skillId === Number(ability.skillId))?.prereqs || [],
                    })),
                ]);
                const previous = synced.current;
                const changedTransform = previous.transformId !== transformId;
                const transformIds = new Set([...previous.skills, ...base, ...skills]
                    .filter(skill => skill.skillset === 8).map(skill => skill.skillId));
                setCurrentCharacter(prev => {
                    if (!active() || !matches(prev, scope)) return prev;
                    const purchases = (prev.skills || []).filter(skill => !changedTransform ||
                        (Number(skill.skillset) !== 8 && !transformIds.has(Number(skill.skillId))));
                    const next = updateCalculatedFields({ ...prev, skills: addStaticSkills(purchases, skills) }, skills);
                    return shallowEqualCharacter(prev, next) ? prev : next;
                });
                synced.current = { characterId, transformId, skills };
                setState({ scope, skills, loading: false, error: null });
            } catch {
                if (!active()) return;
                const error = 'Failed to load available skills.';
                setState(prev => ({ ...prev, scope, loading: false, error }));
                toast.error(error);
            }
        })();
        return () => { cancelled = true; };
    }, [scope, characterId, transformId, token, setCurrentCharacter, revision]);

    const ready = state.scope === scope && !state.loading && !state.error && !!characterId;
    // Keep the last catalog for display during a transform reload/failure only.
    // Readiness still gates edits, printing and draft application. Never expose
    // another character's or authentication scope's catalog.
    const skills = state.scope?.characterId === characterId && state.scope?.token === token
        ? state.skills : EMPTY_SKILLS;
    useEffect(() => {
        if (!ready) return;
        setCurrentCharacter(prev => {
            if (currentScope.current !== scope || !matches(prev, scope)) return prev;
            const next = updateCalculatedFields(prev, skills);
            return shallowEqualCharacter(prev, next) ? prev : next;
        });
    }, [scope, ready, skills, setCurrentCharacter,
        currentCharacter?.skillsetPrimary, currentCharacter?.skillsetSecondary,
        currentCharacter?.skillPointsEffective, currentCharacter?.skillPointsTotal]);

    return { skills, ready, characterId, transformId,
        error: state.scope === scope ? state.error : null,
        retry: () => {
            setState(prev => ({ ...prev, loading: true, error: null }));
            setRevision(value => value + 1);
        } };
}
